import { useContext, useMemo, useState } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import {
  Project,
  ProjectFilters,
  ProjectFiltersTranslator,
  projectsConstants,
  ProjectSearchTermOptions,
} from "../../api/Projects";
import { ProjectFilterBar, ProjectFilterForm } from "./ProjectFilterBar";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { GetPersons } from "../../common/misc/EntityRenders/EntityRenderer";
import { ExportCsvButton } from "../../common/tables/ExportCsvButton/ExportCsvButton";
import { AddCustomTypeEntity } from "../../Customization/CustomTypes/generics/AddCustomTypeEntity";
import { useCustomTypesEntityTable } from "../../Customization/CustomTypes/generics/useCustomTypesEntityTable";
import { renderCustomTypeColumn } from "../../Customization/CustomTypes/generics/CustomTypeRenderer";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
  UseITypedEntityTableProps,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityTableEditButton } from "../../common/entity/entityComponents/EntityTableEditButton";
import { EntityTableCloneButton } from "../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableDeleteButton } from "../../common/entity/entityComponents/EntityTableDeleteButton";
import { GenericEntityTableComplianceDropdown } from "../../Entities/GenericComponents/common/GenericEntityTable";
import SearchInputWithOptions from "../../common/forms/SearchInputWithOptions/SearchInputWithOptions";
import { NestedDropdown } from "../../common/buttons/NestedDropdown/NestedDropdown";
import { ExportPdfButton } from "../../common/tables/ExportPdfButton/ExportPdfButton";
import { EntityOrderBy, InferSortByOptions, ISearchTermParametersDefaults } from "../../api/GenericTypes";

export const switchProjectsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<ProjectFilters>>[] | null | undefined
): SortState<InferSortByOptions<ProjectFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "DATASET_COUNT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-stats-datasets",
          property: sort.property,
        };
      case "INVENTORY_ITEM_COUNT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-stats-inventory-items",
          property: sort.property,
        };
      case "LAB_NOTEBOOK_COUNT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-stats-lab-notebooks",
          property: sort.property,
        };
      case "SAMPLE_COUNT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-stats-samples",
          property: sort.property,
        };
      default:
        return {
          sortDirection: "ASC",
          headerId: "default-name",
          property: "NAME",
        };
    }
  });
};
export const defaultProjectsFilters: ProjectFilters = {
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
  ...ISearchTermParametersDefaults,
};

export const useProjectsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"projects">) => {
  const { session } = useContext(SessionContext);

  const defaults: ColumnsSettings<Project> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-customType": { pos: 2, active: true, header: fieldLabels.customType, property: "customType" },
      "default-enteredOn": { pos: 4, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 5, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 6, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 7, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [
      fieldLabels.enteredBy,
      fieldLabels.enteredOn,
      fieldLabels.customType,
      fieldLabels.id,
      fieldLabels.modifiedBy,
      fieldLabels.modifiedOn,
      fieldLabels.name,
    ]
  );

  const tabStoreDefaults: TableTabsDict<Project, ProjectFilters, ProjectFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultProjectsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      mydata: {
        tabId: "mydata",
        type: "fixed",
        label: `My ${projectsConstants.entityPlural}`,
        title: `My ${projectsConstants.entityPlural}`,
        icon: "user",
        align: "left",
        xPos: 1,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultProjectsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { personIds: session?.person.id ? [session.person.id] : null },
          sidebarFilters: {},
        },
      },
    }),
    [session?.person.id]
  );

  return { defaults, tabStoreDefaults };
};

export const useProjectsTableColumns = ({
  entityConstants,
  fieldLabels,
  defaults,
  filters,
  sort,
  setSort,
}: UseITypedEntityTableProps<"projects">) => {
  // Custom Fields
  const { columnSettings, customFieldHeaders } = useCustomTypesEntityTable({
    defaultColumnSettings: defaults,
    entityType: "Project",
    customTypeIds: filters?.customTypeIds ?? undefined,
    sort,
    setSort,
  });

  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<Project> = [
      {
        id: "default-id",
        Header: "Project-ID",
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    // Add custom field headers
    headers.push(renderCustomTypeColumn(entityConstants, sort, setSort));
    headers = headers.concat(customFieldHeaders);

    return headers;
  }, [
    customFieldHeaders,
    entityConstants,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    fieldLabels.name,
    setSort,
    sort,
  ]);

  return { columns, columnSettings };
};

export const ProjectsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"projects">) => {
  const [showModal, setShowModal] = useState(false);

  const { bulkDeleteMutationAsync: deleteProject, isLoadingBulkDeleteMutation } = entityApi;
  const { defaults, tabStoreDefaults } = useProjectsTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<Project>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<Project, ProjectFilters, ProjectFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, onTabChange, sort, setSort, searchValue, setSearchValue, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchProjectsDefaultSortState,
      dispatchTabStore,
    });

  const { columns, columnSettings } = useProjectsTableColumns({
    fieldLabels,
    entityConstants,
    defaults,
    filters,
    sort,
    setSort,
  });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <AddCustomTypeEntity
            entityConstants={entityConstants}
            canCreateEntityPermission={permissions.canCreate}
            entityType={"Project"}
          />
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <ProjectFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<Project, ProjectFilters>
                filters={filters}
                excludeFilters={{
                  searchTermIncludeIds: () => false,
                  searchTermIncludeNames: () => false,
                  searchTermIncludeUuids: () => false,
                  searchTermIncludeCustomFieldValues: () => false,
                }}
                translatorConsts={ProjectFiltersTranslator}
              />
              <SearchInputWithOptions
                searchTermOptions={ProjectSearchTermOptions}
                filters={filters}
                setFilters={async (newFilters) => {
                  await dispatchTabStore({
                    type: "setTab",
                    options: { keepPrevious: true },
                    payload: { settings: { filters: { searchTerm: debouncedSearchValue, ...newFilters } } },
                  });
                }}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
              />
              {/* <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" /> */}
              <div>
                <EntityTableEditButton
                  entityConstants={entityConstants}
                  routes={routes}
                  permissions={permissions}
                  selection={selection}
                  selectionPermissions={selectionPermissions}
                />
              </div>
              <div>
                <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                  <li>
                    <EntityTableCloneButton
                      entityConstants={entityConstants}
                      routes={routes}
                      permissions={permissions}
                      selection={selection}
                    />
                  </li>
                  <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                    <li>
                      <ExportCsvButton
                        entityConstants={entityConstants}
                        columnSettings={columnSetting}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                    <li>
                      <ExportPdfButton
                        entityConstants={entityConstants}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                  </NestedDropdown>
                  {/* Locking & Signing */}
                  <GenericEntityTableComplianceDropdown
                    entityTypeId={entityTypeId}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    tableFunctionRef={functionRef}
                    currentTab={currentTab}
                  />
                  <li className="divider" />
                  <li>
                    <EntityTableDeleteButton
                      entityConstants={entityConstants}
                      permissions={permissions}
                      selection={selection}
                      selectionPermissions={selectionPermissions}
                      onClick={() => setShowModal(true)}
                    />
                  </li>
                </MoreDropdown>
              </div>
              <AlertModal
                type="danger"
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select ${entityConstants.entitySingular} to delete`
                    : selection.size === 1
                    ? `Delete the selected ${entityConstants.entitySingular}`
                    : `Delete the selected ${entityConstants.entityPlural}`
                }`}
                description={`Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`}
                proceedLabel="Delete"
                onProceed={async () => {
                  await deleteProject({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.entityPlural,
                    params: { deletePermanently: true },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<Project, ProjectFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={columnSettings}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
